import { AuthType, User as UserPermissionType } from "@runn/permissions"
import React, { useMemo, useRef, useState } from "react"
import { Mention, MentionsInput } from "react-mentions"
import { graphql, useLazyLoadQuery } from "react-relay"

import styles from "./NoteInput.module.css"

import { NoteInputQuery } from "./__generated__/NoteInputQuery.graphql"

import Button from "~/common/buttons/Button"
import * as icons from "~/common/react-icons"
import MentionTriggerIcon from "~/common/react-icons/mentionTrigger"

import { useEntitlementSwitch } from "~/Entitlements/useEntitlements"

import { Permissions } from "../types"

type Props = {
  onSubmit?: (note: string) => Promise<void>
  onClose?: () => void
  submitButtonText?: string
  initialValue?: string
  onValueChange?: (value: string) => void
  forceSuggestionsAboveCursor: boolean
  autoFocus?: boolean
  rows?: number
  optional?: boolean
  placeholder?: string
  onEnter?: () => void
  permissions: Pick<Permissions, "canUserView">
}

const noteInputQuery = graphql`
  query NoteInputQuery {
    current_account {
      id
      timesheets_protected
      user_accounts {
        id
        permissions
        user {
          id
          first_name
          last_name
          email
          manageable_projects {
            id
            project {
              id
            }
          }
          manageable_people {
            id
            person {
              id
            }
          }
          manageable_project_teams {
            id
            team {
              id
            }
          }
          linkedPerson {
            id
          }
        }
      }
    }
  }
`

type User =
  NoteInputQuery["response"]["current_account"]["user_accounts"][number]["user"] & {
    id: string
    fullName: string
  }

const NoteInput = ({
  onSubmit,
  onClose,
  initialValue,
  submitButtonText,
  onValueChange,
  forceSuggestionsAboveCursor,
  autoFocus,
  rows,
  optional,
  placeholder,
  onEnter,
  permissions,
}: Props) => {
  const isEntitledToNotifications = useEntitlementSwitch("notifications")

  const {
    current_account: {
      user_accounts,
      id: currentAccountId,
      timesheets_protected,
    },
  } = useLazyLoadQuery<NoteInputQuery>(noteInputQuery, {})

  const users: User[] = useMemo(() => {
    return user_accounts
      .reduce((acc, userAccount) => {
        const user = userAccount.user
        const userToCheck: UserPermissionType = {
          authType: AuthType.User,
          id: user.id,
          email: user.email,
          account: {
            id: currentAccountId,
            timesheets_protected: timesheets_protected ?? false,
          },
          permissions: userAccount.permissions,
          manageable_projects: user.manageable_projects,
          manageable_people: user.manageable_people,
          manageable_project_teams: user.manageable_project_teams,
          person: user.linkedPerson,
        }

        if (permissions.canUserView(userToCheck)) {
          const fullName = `${user.first_name} ${user.last_name}`
          acc.push({
            id: String(user.id),
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            fullName,
          })
        }
        return acc
      }, [])
      .sort((a, b) => a.fullName.localeCompare(b.fullName))
  }, [user_accounts, permissions, currentAccountId, timesheets_protected])

  const [note, setNote] = useState(initialValue || "")
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const mentionsInstanceRef = useRef<any>(null)

  const isSubmitDisabled = !optional && note.trim() === ""

  const createNote = async () => {
    const trimmedNote = note.trim()

    if (!trimmedNote) {
      return
    }

    await onSubmit?.(trimmedNote)
    setNote("")
  }

  const handleShortcut = async (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (isSubmitDisabled) {
      return
    }

    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      await createNote()
      onEnter?.()
    }
  }

  const onMentionTrigger = () => {
    if (inputRef.current && mentionsInstanceRef.current) {
      const currentValue = inputRef.current.value
      const cursorPosition = inputRef.current.selectionStart
      const newValue =
        currentValue.slice(0, cursorPosition) +
        "@" +
        currentValue.slice(cursorPosition)
      mentionsInstanceRef.current.setState((prev) => ({
        ...prev,
        selectionStart: cursorPosition,
        selectionEnd: cursorPosition,
      }))
      setTimeout(() => {
        if (inputRef.current && mentionsInstanceRef.current) {
          inputRef.current.value = newValue
          inputRef.current.focus()
          mentionsInstanceRef.current.handleChange({
            target: {
              selectionStart: cursorPosition + 1,
              selectionEnd: cursorPosition + 1,
              value: newValue,
            },
            nativeEvent: { isComposing: true },
          })
        }
      }, 0)
    }
  }

  return (
    <div>
      <div className={styles.commentBox}>
        <MentionsInput
          ref={mentionsInstanceRef}
          value={note}
          onChange={(e) => {
            setNote(e.target.value)
            onValueChange?.(e.target.value)
          }}
          onKeyDown={handleShortcut}
          inputRef={inputRef}
          style={{
            control: {
              backgroundColor: "#fff",
              fontSize: 14,
              fontWeight: "normal",
            },
            input: {
              overflow: "auto",
              height: 70,
              border: "none",
              lineHeight: "unset",
            },
            highlighter: {
              boxSizing: "border-box",
              overflow: "hidden",
              height: 70,
              border: "none",
              padding: 2,
            },
            "&multiLine": {
              input: {
                overflow: "auto",
                height: 70,
                border: "none",
                lineHeight: "unset",
              },
              highlighter: {
                boxSizing: "border-box",
                overflow: "hidden",
                height: 70,
                border: "none",
                padding: 2,
              },
            },
            suggestions: {
              zIndex: 50,
              left: -13,
              minWidth: 355,
              margin: 0,
              list: {
                marginBottom: 0,
                marginTop: forceSuggestionsAboveCursor ? 0 : 20,
                overflow: "auto",
                ...(forceSuggestionsAboveCursor === false
                  ? {
                      position: "fixed",
                    }
                  : {}),
                maxHeight: 300,
                backgroundColor: "white",
                boxShadow:
                  "0px 2px 4px 0px rgba(0, 0, 0, 0.10), 0px 3px 6px 0px rgba(0, 0, 0, 0.15)",
                width: 355,
                borderRadius: 4,
              },
              item: {
                fontSize: 13,
                color: "var(--midnight)",
                padding: 12,
                "&focused": {
                  backgroundColor: "var(--snow)",
                },
              },
            },
          }}
          placeholder={placeholder || "Add notes, important links, etc."}
          autoFocus={autoFocus}
          rows={rows}
          required={!optional}
          data-test="note-input"
          id="note-input"
          forceSuggestionsAboveCursor={forceSuggestionsAboveCursor}
          allowSpaceInQuery={true}
        >
          <Mention
            trigger="@"
            appendSpaceOnAdd={true}
            data={(s) => {
              if (!isEntitledToNotifications) {
                return []
              }

              return users.filter((u) => {
                const searchTerms = s.toLowerCase().trim().split(/\s+/)
                return searchTerms.every(
                  (term) =>
                    u.fullName.toLowerCase().includes(term) ||
                    u.email.toLowerCase().includes(term),
                )
              })
            }}
            renderSuggestion={(suggestion: User) => (
              <div
                data-test="mention-suggestion"
                className={styles.mentionSuggestion}
              >
                <strong data-test="mention-suggestion-name">
                  {suggestion.fullName}
                </strong>
                <span>{suggestion.email}</span>
              </div>
            )}
            style={{
              textDecoration: "underline",
              textDecorationColor: "var(--smoke)",
              textUnderlinePosition: "under",
              textUnderlineOffset: "1px",
            }}
            markup="<U-__id__>"
            displayTransform={(id, display) => {
              const user = users.find((u) => u.id === id)
              return user ? `@${user.fullName}` : display
            }}
          />
        </MentionsInput>
      </div>
      <div className={styles.actionsContainer}>
        {isEntitledToNotifications && (
          <span
            data-test="mention-trigger"
            className={styles.mentionTrigger}
            onClick={onMentionTrigger}
          >
            <MentionTriggerIcon />
          </span>
        )}
        {onSubmit && (
          <div className={styles.actions}>
            {onClose && <Button text="Cancel" onClick={onClose} />}
            <Button
              className={submitButtonText ? undefined : styles.sendBtn}
              onClick={createNote}
              outlined={false}
              icon={submitButtonText ? undefined : <icons.Send />}
              text={submitButtonText}
              disabled={isSubmitDisabled}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default NoteInput
